// Pistes documentées (sans IA) + assistance optionnelle. Chaque piste ouvre sa source à la bonne page.
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { BookOpen, Plus, Sparkles } from 'lucide-react';
import { api, messageErreur } from '../../lib/api.ts';
import { useSession } from '../../lib/session.tsx';
import { pluriel } from '../../lib/format.ts';
import {
  Alerte, Badge, Bouton, ChampTexte, Chargement, Dialogue, EtatErreur, EtatVide, LienBouton, MarqueIa, Panneau, Surligne, useNotifier,
} from '../../components/ui.tsx';
import { SourceExtrait } from './composants.tsx';
import type { Intervention, Piste, ResultatPistes } from './types.ts';

/** Texte de départ d'un contrôle tiré d'une piste : la première phrase de l'extrait, sans marqueurs. Le technicien la reformule. */
function texteDepuisPiste(p: Piste) {
  const lignes = p.extrait.replace(/[\u0001\u0002]/g, '').split(/\n+/).map((l) => l.trim()).filter(Boolean);
  // Un titre de section en tête (« 6.3 Codes défaut ») n'est pas une action : on le retire.
  while (lignes.length > 1 && /^(\d+(\.\d+)*\.?\s+)?[^.:;]{0,60}$/.test(lignes[0]) && /^\d/.test(lignes[0])) lignes.shift();
  const brut = lignes.join(' ').replace(/\s+/g, ' ').trim();
  const phrase = brut.split(/(?<=[.!?])\s/)[0] ?? brut;
  return `Vérifier : ${phrase.length > 220 ? `${phrase.slice(0, 219)}…` : phrase}`;
}

export function PanneauPistes({ intervention: i, appliquer }: { intervention: Intervention; appliquer: (r: Intervention) => void }) {
  const { peut, etat } = useSession();
  const notifier = useNotifier();
  const codes = (i.context?.codes ?? []).join(',');
  const q = useQuery({
    queryKey: ['int-pistes', i.id, i.symptom, codes, i.equipment_id],
    queryFn: () => api.get<ResultatPistes>(`/api/interventions/${i.id}/pistes`),
  });
  const [ajout, setAjout] = useState<Piste | null>(null);
  const [texte, setTexte] = useState('');
  const [justif, setJustif] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [assistance, setAssistance] = useState(false);
  const [messageIa, setMessageIa] = useState<string | null>(null);
  const ecrire = peut('intervention.ecrire') && i.modifiable;
  const iaDisponible = !!etat?.ia.disponible && !!etat?.ia.documentsClientsAutorises;

  const ouvrir = (p: Piste) => {
    setAjout(p);
    setTexte(texteDepuisPiste(p));
    setJustif(`D’après « ${p.titre} », version ${p.version_label}, p. ${p.page}${p.localisation ? `, ${p.localisation}` : ''}.`);
  };
  const ajouter = async () => {
    if (!ajout || !texte.trim()) return;
    setEnvoi(true);
    try {
      const r = await api.post<{ intervention: Intervention }>(`/api/interventions/${i.id}/elements`, {
        kind: 'controle_propose', text: texte.trim(), justification: justif.trim() || null, status: 'a_faire', origin: 'regle',
        source: { versionId: ajout.version_id, page: ajout.page, chunkId: ajout.chunk_id },
      });
      appliquer(r.intervention);
      setAjout(null);
      notifier('Contrôle proposé ajouté.');
      q.refetch();
    } catch (e) { notifier(messageErreur(e), true); } finally { setEnvoi(false); }
  };
  const demanderAssistance = async () => {
    setAssistance(true); setMessageIa(null);
    try {
      const r = await api.post<{ ajoutes: number; rejetes: number; message: string | null; intervention: Intervention }>(`/api/interventions/${i.id}/assistance`);
      appliquer(r.intervention);
      setMessageIa(r.message ?? `${pluriel(r.ajoutes, 'contrôle proposé ajouté', 'contrôles proposés ajoutés')}, à vérifier.${r.rejetes ? ` ${pluriel(r.rejetes, 'proposition écartée', 'propositions écartées')} faute de source.` : ''}`);
    } catch (e) { setMessageIa(messageErreur(e)); } finally { setAssistance(false); }
  };

  const liste = (pistes: Piste[]) => (
    <ul className="int-pistes">
      {pistes.map((p) => (
        <li key={p.chunk_id} className="int-piste">
          <div className="rangee" style={{ gap: 6 }}>
            {p.code_cite && <Badge ton="accent" icone={false}>Code défaut cité</Badge>}
            {p.exact && <Badge ton="accent" icone={false}>Référence exacte</Badge>}
            {p.version === 'non_confirmee' && <Badge ton="ambre">Version non confirmée par la source</Badge>}
            {p.version === 'confirmee' && <Badge ton="succes">Version couverte</Badge>}
          </div>
          <p className="extrait"><Surligne texte={p.extrait} /></p>
          {p.ocr && <MarqueIa />}
          <SourceExtrait versionId={p.version_id} page={p.page} chunkId={p.chunk_id} titre={p.titre} version={p.version_label} localisation={p.localisation} />
          {p.versions_couvertes && <span className="tres-petit discret expert-seulement">Versions couvertes : {p.versions_couvertes}</span>}
          {ecrire && (p.deja_ajoutee
            ? <span className="petit discret">Déjà retenue comme contrôle proposé.</span>
            : <div><Bouton compact icone={<Plus aria-hidden />} onClick={() => ouvrir(p)}>Ajouter comme contrôle proposé</Bouton></div>)}
        </li>
      ))}
    </ul>
  );

  return (
    <Panneau titre="Pistes documentées" id="pistes" className="int-o-pistes"
      actions={ecrire && (iaDisponible
        ? <Bouton compact icone={<Sparkles aria-hidden />} chargement={assistance} onClick={demanderAssistance}>Proposer des contrôles</Bouton>
        : <Badge ton="neutre" titre={etat?.ia.raison}>Assistance non configurée</Badge>)}>
      <div className="pile">
        {messageIa && <Alerte ton="info" titre="Assistance">{messageIa} Les contrôles proposés par l’assistance sont marqués et restent à vérifier.</Alerte>}
        {q.isLoading && <Chargement lignes={3} libelle="Recherche dans la documentation…" />}
        {q.isError && <EtatErreur erreur={q.error} reessayer={() => q.refetch()} />}
        {q.data && (
          <>
            <p className="tres-petit discret">
              Recherche : {[...q.data.criteres.references.map((r) => r), ...q.data.criteres.termes].join(', ') || 'aucun critère'}
              {q.data.criteres.fabricant ? ` — fabricant ${q.data.criteres.fabricant}` : ''}
            </p>
            {q.data.validees.length === 0 && q.data.nonValidees.length === 0 && (
              <EtatVide icone={<BookOpen />} titre="Aucune piste documentée"
                texte={i.equipement?.reference
                  ? <>Aucun document consultable ne cite <span className="mono">{i.equipement.reference}</span> ni les codes saisis. Importez la notice ou la liste des codes défaut du fabricant.</>
                  : 'Renseignez la référence exacte de l’équipement et les codes défaut affichés pour retrouver la documentation.'}
                action={peut('doc.importer') ? <LienBouton compact to="/documentation/importer">Importer un document</LienBouton> : undefined} />
            )}
            {q.data.validees.length > 0 && (
              <section className="pile" aria-label="Documents validés">
                <h3 className="petit">Documents validés</h3>
                {liste(q.data.validees)}
              </section>
            )}
            {q.data.nonValidees.length > 0 && (
              <section className="pile" aria-label="Documents non validés">
                <Alerte ton="ambre" titre="Documents non validés">Ces extraits n’ont pas encore été relus par un référent : à confirmer avant de s’y fier.</Alerte>
                {liste(q.data.nonValidees)}
              </section>
            )}
          </>
        )}
      </div>
      <Dialogue titre="Ajouter comme contrôle proposé" ouvert={!!ajout} fermer={() => setAjout(null)}
        pied={<><Bouton onClick={() => setAjout(null)}>Annuler</Bouton><Bouton variante="principal" chargement={envoi} disabled={!texte.trim()} onClick={ajouter}>Ajouter</Bouton></>}>
        {ajout && (
          <div className="pile">
            <ChampTexte libelle="Contrôle à réaliser" rows={3} value={texte} onChange={(e) => setTexte(e.target.value)} aide="Reformulez l’action concrète à mener sur place." />
            <ChampTexte libelle="Justification" rows={2} value={justif} onChange={(e) => setJustif(e.target.value)} />
            <SourceExtrait versionId={ajout.version_id} page={ajout.page} chunkId={ajout.chunk_id} titre={ajout.titre} version={ajout.version_label} localisation={ajout.localisation} />
          </div>
        )}
      </Dialogue>
    </Panneau>
  );
}
